import { createSelector } from 'reselect';
import { Map } from 'immutable';

import { GlobalState } from './reducers';
import {
  constants as realDeviceSelectionConstants,
  records as realDeviceSelectionRecords,
} from './ducks/realDeviceSelection';
import testConfigurationSelectors from './ducks/testConfiguration/selectors';
import { selectors as developerDrawerSelectors } from './ducks/developerDrawer';
import { selectors as rdcSelectors } from './ducks/rdcSession';

const {
  CHANGE_LOCALE,
  PRESS_BACK,
  PRESS_MENU,
  PRESS_SEARCH,
  EXECUTE_SHELL_COMMAND,
  TOGGLE_SYSTEM_ANIMATIONS,
  TOGGLE_WIFI,
} = realDeviceSelectionConstants.MOBILE_DEVICE_FEATURES;

const realDeviceSessionSelector = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (store: GlobalState): Map<string, any> => store.apps.manual.session
);

const deviceDescriptorSelector = createSelector([
  realDeviceSessionSelector,
],
(session) => (
  session?.get('deviceDescriptor')) as realDeviceSelectionRecords.DeviceDescriptor,
);

const realDeviceClientSelector = createSelector([
  realDeviceSessionSelector,
],
(session) => (
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  session?.get('device')),
);

const mobileSessionSelector = createSelector([
  rdcSelectors.sessionClosedReasonSelector,
  realDeviceSessionSelector,
  developerDrawerSelectors.developerDrawerSelector,
  testConfigurationSelectors.realDeviceConfigurationSelector,
  deviceDescriptorSelector,
  realDeviceClientSelector,
],
(
  sessionClosedReason,
  session,
  developerDrawer,
  realDeviceConfiguration,
  deviceDescriptor,
  realDeviceClient,
) => ({
  animationsEnabled: !!session?.get('deviceAnimationsEnabled'),
  appVersion: session?.get('appVersion'), // TODO: check if really used somewhere
  changeLocaleSupported: deviceDescriptor?.isFeatureSupported(CHANGE_LOCALE),
  device: realDeviceClient,
  deviceDescriptor,
  deviceOrientation: session.get('deviceOrientation'),
  deviceToolsDisabled: !session.get('deviceToolsEnabled'),
  isAndroidDevice: deviceDescriptor?.os === realDeviceSelectionConstants.MOBILE_PLATFORMS.ANDROID,
  isDeveloperDrawerHorizontal: developerDrawer.isHorizontal,
  isMuteSupported: deviceDescriptor?.isWebRtcSupported(),
  isWebRtcAudioEnabled: !!session.get('isWebRtcAudioEnabled'),
  isPerformanceModeEnabled: !!session.get('isPerformanceModeEnabled'),
  isViewOnly: realDeviceClient?.deviceSessionDescriptor.viewOnly !== false,
  pressBackSupported: deviceDescriptor?.isFeatureSupported(PRESS_BACK),
  pressMenuSupported: deviceDescriptor?.isFeatureSupported(PRESS_MENU),
  pressSearchSupported: deviceDescriptor?.isFeatureSupported(PRESS_SEARCH),
  realDeviceConfiguration,
  session,
  sessionClosedReason,
  sessionState: session.get('sessionState'),
  shellCommandSupported: deviceDescriptor?.isFeatureSupported(EXECUTE_SHELL_COMMAND),
  takeScreenshotEnabled: !!session.get('device')?.deviceSessionDescriptor?.deviceSessionId,
  toggleSystemAnimationsSupported: deviceDescriptor?.isFeatureSupported(TOGGLE_SYSTEM_ANIMATIONS),
  toggleWifiSupported: deviceDescriptor?.isFeatureSupported(TOGGLE_WIFI),
  wifiEnabled: session.get('deviceWifiEnabled'),
}));

export {
  deviceDescriptorSelector,
  mobileSessionSelector,
  realDeviceClientSelector,
  realDeviceSessionSelector,
};
